import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

export const CustomDatePickerFixed = ({ value, viewDate, onChange, disabled, className, error, placeholder = "dd-mm-yyyy", events = [], customTrigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days', 'months', 'years'
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const parseLocalDate = (val) => {
    if (!val) return new Date();
    if (typeof val === 'string' && val.includes('-')) {
      const parts = val.split('-');
      if (parts.length === 3) {
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      }
    }
    return new Date(val);
  };

  const initialDate = parseLocalDate(value);
  const [currentYear, setCurrentYear] = useState(() => {
    return value ? parseLocalDate(value).getFullYear() : new Date().getFullYear();
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    return value ? parseLocalDate(value).getMonth() : new Date().getMonth();
  });
  const [hoveredDate, setHoveredDate] = useState(null);

  useEffect(() => {
    if (viewDate && !isOpen) {
      const vd = parseLocalDate(viewDate);
      setCurrentYear(vd.getFullYear());
      setCurrentMonth(vd.getMonth());
    }
  }, [viewDate, isOpen]);


  const displayValue = value ? (() => {
    const d = new Date(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  })() : '';

  const [inputValue, setInputValue] = useState(displayValue);

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  const handleInputChange = (e) => {
    let val = e.target.value;
    
    // Check if it's already a complete YYYY-MM-DD date (like from paste)
    const yyyyMmDd = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;
    if (yyyyMmDd.test(val)) {
       setInputValue(val);
       const parsedDate = new Date(val);
       if (parsedDate && !isNaN(parsedDate.getTime())) {
         const iso = parsedDate.toISOString().slice(0, 10);
         onChange(iso);
         setCurrentYear(parsedDate.getFullYear());
         setCurrentMonth(parsedDate.getMonth());
       }
       return;
    }

    // Strip everything except digits and separators
    let cleaned = val.replace(/[^\d\-/.]/g, '');
    
    // Auto-insert hyphens if user is typing forward
    const isTypingForward = cleaned.length > inputValue.length;
    
    // Remove all non-digits to build standard DD-MM-YYYY
    let digits = cleaned.replace(/\D/g, '').substring(0, 8);
    
    if (digits.length >= 2) {
      let d = parseInt(digits.substring(0, 2), 10);
      if (d > 31) d = 31;
      digits = String(d).padStart(2, '0') + digits.substring(2);
    }
    if (digits.length >= 4) {
      let m = parseInt(digits.substring(2, 4), 10);
      if (m > 12) m = 12;
      digits = digits.substring(0, 2) + String(m).padStart(2, '0') + digits.substring(4);
    }
    
    let formattedValue = '';
    if (digits.length > 0) {
      formattedValue = digits.substring(0, 2);
    }
    if (digits.length >= 2) {
      if (isTypingForward && digits.length === 2) {
        formattedValue += '-';
      } else if (digits.length > 2) {
        formattedValue += '-' + digits.substring(2, 4);
      }
    }
    if (digits.length >= 4) {
      if (isTypingForward && (digits.length === 4 || digits.length === 6)) {
        formattedValue += '-';
      } else if (digits.length > 4) {
        formattedValue += '-' + digits.substring(4, 8);
      }
    }

    setInputValue(formattedValue);
    if (e.target.value !== formattedValue) {
      setTimeout(() => {
        if (e.target) {
          e.target.value = formattedValue;
        }
      }, 0);
    }
    
    // Try to parse the complete date
    if (digits.length === 8 || digits.length === 6) {
      const d = digits.substring(0, 2);
      const m = digits.substring(2, 4);
      let y = digits.substring(4, 8);
      
      if (digits.length === 6) {
        const yNum = parseInt(y, 10);
        y = yNum > 50 ? `19${y}` : `20${y}`;
      }
      
      const parsedDate = new Date(`${y}-${m}-${d}`);
      
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const iso = parsedDate.toISOString().slice(0, 10);
        onChange(iso);
        setCurrentYear(parsedDate.getFullYear());
        setCurrentMonth(parsedDate.getMonth());
      }
    } else if (digits === '') {
      onChange('');
    }
  };

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popupHeight = 300; 
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const isUpward = spaceBelow < popupHeight && spaceAbove > spaceBelow;

      setDropdownStyle({
        position: 'fixed',
        ...(isUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
        left: rect.left + (rect.width / 2) - (240 / 2),
        width: 240,
        zIndex: 99999,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setView('days');
      return;
    }

    const initD = parseLocalDate(value);
    setCurrentYear(initD.getFullYear());
    setCurrentMonth(initD.getMonth());

    const handleOutsideClick = (event) => {
      const clickedInContainer = containerRef.current && containerRef.current.contains(event.target);
      const clickedInDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!clickedInContainer && !clickedInDropdown) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const selectDate = (day) => {
    const y = currentYear;
    const m = String(currentMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${y}-${m}-${d}`);
    setIsOpen(false);
  };

  const clearDate = () => {
    onChange('');
    setIsOpen(false);
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const prevMonthDays = getDaysInMonth(currentMonth === 0 ? currentYear - 1 : currentYear, currentMonth === 0 ? 11 : currentMonth - 1);

  const grid = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    grid.push({ day: prevMonthDays - i, isCurrentMonth: false });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    grid.push({ day: i, isCurrentMonth: true });
  }
  const remainingCells = 42 - grid.length;
  for (let i = 1; i <= remainingCells; i++) {
    grid.push({ day: i, isCurrentMonth: false });
  }

  const selectedDateObj = value ? parseLocalDate(value) : null;
  const isSelected = (day) => {
    if (!selectedDateObj) return false;
    return selectedDateObj.getDate() === day &&
           selectedDateObj.getMonth() === currentMonth &&
           selectedDateObj.getFullYear() === currentYear;
  };

  const renderDaysView = () => (
    <>
      <div className="flex items-center justify-between mb-2 px-1">
        <button type="button" onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-full text-[var(--theme-primary)] transition-colors">
          <ChevronLeft size={16} />
        </button>
        <button 
          type="button" 
          onClick={() => setView('years')} 
          className="text-[13px] font-bold text-[var(--theme-primary)] hover:opacity-80 hover:bg-slate-100 px-2 py-0.5 rounded transition-colors"
        >
          {MONTHS[currentMonth]} {currentYear}
        </button>
        <button type="button" onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-full text-[var(--theme-primary)] transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1 border-b border-gray-100 pb-1">
        {DAYS.map(day => (
          <div key={day} className="text-center text-[10px] font-bold text-gray-400 py-0.5">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0 gap-x-0.5">
        {grid.map((cell, index) => {
          const isSelectedDay = cell.isCurrentMonth && isSelected(cell.day);
          const y = currentYear;
          const m = String(currentMonth + 1).padStart(2, '0');
          const d = String(cell.day).padStart(2, '0');
          const dateStr = `${y}-${m}-${d}`;

          let dayEvents = [];
          if (cell.isCurrentMonth && events.length > 0) {
            dayEvents = events.filter(e => {
                if (typeof e === 'string') return e === dateStr;
                return e && e.date === dateStr;
            });
          }
          const hasEvent = dayEvents.length > 0;
          const tooltipText = dayEvents.map(e => e.title || '').filter(Boolean).join('\n');

          const today = new Date();
          const isToday = today.getDate() === cell.day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;

          return (
            <div key={index} className="relative flex justify-center items-center w-full">
              <button
                type="button"
                onMouseEnter={() => hasEvent ? setHoveredDate(dateStr) : undefined}
                onMouseLeave={() => setHoveredDate(null)}
                onClick={() => cell.isCurrentMonth && selectDate(cell.day)}
                disabled={!cell.isCurrentMonth}
                className={`
                  relative flex justify-center items-center w-7 h-7 rounded-full text-xs font-semibold
                  transition-all duration-200
                  ${!cell.isCurrentMonth ? 'text-slate-300 cursor-default' : 'text-slate-700 hover:bg-slate-100 hover:text-[var(--theme-primary)]'}
                  ${isToday && !isSelectedDay ? 'bg-blue-50 text-blue-600' : ''}
                  ${isSelectedDay ? 'bg-[var(--theme-primary)] text-white shadow-md' : ''}
                `}
              >
                {cell.day}
                {hasEvent && (
                  <div className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isSelectedDay ? 'bg-white' : 'bg-[var(--theme-primary)]'}`}></div>
                )}
              </button>

              {hoveredDate === dateStr && hasEvent && (
                <div 
                  className="absolute bottom-full mb-1 z-[9999] pointer-events-none drop-shadow-xl" 
                  style={{ 
                    ...(index % 7 <= 2 ? { left: '0' } : index % 7 >= 4 ? { right: '0' } : { left: '50%', transform: 'translateX(-50%)' })
                  }}
                >
                  <div className="bg-white text-slate-800 rounded-lg shadow-2xl border border-sky-100 flex flex-col w-[170px] overflow-hidden relative">
                    <div className="bg-sky-50 px-2.5 py-1.5 border-b border-sky-100 flex justify-between items-center gap-2">
                       <span className="font-bold text-[10px] text-sky-900">
                         {new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                       </span>
                       <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">{dayEvents.length} Appt{dayEvents.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex flex-col">
                      {dayEvents.slice(0, 3).map((ev, i) => (
                        <div key={i} className="px-3 py-1.5 flex flex-col gap-0 border-b border-slate-100 last:border-0 hover:bg-sky-50/50 text-left transition-colors">
                          <div className="flex justify-between items-start w-full gap-2 mb-0.5">
                            <span className="font-bold text-[10px] text-slate-800 truncate leading-tight">{ev.customerName || 'Unknown'}</span>
                            <span className="text-[10px] font-extrabold uppercase text-sky-600 tracking-widest flex-shrink-0">{ev.status || 'pending'}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate leading-tight">{ev.serviceName}</div>
                          <div className="text-[10px] text-sky-500 font-semibold leading-tight">{ev.time}</div>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="px-2.5 py-1 bg-slate-50 border-t border-slate-100 text-[10px] font-bold text-slate-500 text-center">
                          + {dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                  <div 
                    className="w-3 h-3 bg-white rotate-45 absolute -bottom-1.5 border-b border-r border-sky-100 z-10"
                    style={{
                      ...(index % 7 <= 2 ? { left: '12px' } : index % 7 >= 4 ? { right: '12px' } : { left: '50%', transform: 'translateX(-50%)' })
                    }}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <button type="button" onClick={clearDate} className="text-[11px] font-bold text-gray-500 hover:text-rose-500 transition-colors px-2 py-1 rounded hover:bg-rose-50">
          Clear
        </button>
        <button type="button" onClick={() => setIsOpen(false)} className="text-[11px] font-bold text-gray-500 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100">
          Close
        </button>
      </div>
    </>
  );

  const renderYearsView = () => {
    const currentY = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentY - 80 + i).reverse();

    return (
      <div className="p-1 h-[200px] flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={() => setView('days')} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-bold text-gray-700">Select Year & Month</span>
          <div className="w-6"></div>
        </div>
        <div className="flex-1 overflow-y-auto pr-1 space-y-1 custom-scrollbar">
          {years.map(y => (
            <div key={y} className="mb-3">
              <div className="text-[10px] font-bold text-gray-400 mb-1 pl-1 border-b border-gray-100 pb-0.5">{y}</div>
              <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((m, i) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setCurrentYear(y);
                      setCurrentMonth(i);
                      setView('days');
                    }}
                    className={`
                      py-1 text-[11px] rounded transition-colors text-center
                      ${y === currentYear && i === currentMonth ? 'bg-[var(--theme-primary)] text-white font-bold shadow-sm' : 'text-gray-600 hover:bg-slate-100 hover:text-[var(--theme-primary)]'}
                    `}
                  >
                    {m.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const dropdownContent = isOpen && !disabled ? (
    <div ref={dropdownRef} style={dropdownStyle}>
      <div className="bg-white border border-gray-200 shadow-2xl rounded-xl p-2 font-sans select-none overflow-visible relative" style={{ width: '100%' }}>
        {view === 'days' ? renderDaysView() : renderYearsView()}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${isOpen ? 'z-50' : 'z-20'} ${className?.includes('w-full') ? 'w-full' : ''}`} ref={containerRef}>
      {customTrigger ? (
        <div ref={buttonRef} onClick={() => !disabled && setIsOpen(!isOpen)} className="cursor-pointer inline-flex items-center">
          {customTrigger}
        </div>
      ) : (
        <div
          ref={buttonRef}
          className={`w-full flex items-center gap-2 rounded-lg border ${error ? 'bg-rose-50 border-rose-400' : 'bg-gray-50 border-gray-100'} focus-within:bg-white focus-within:border-[var(--theme-primary)] px-3 py-2 text-xs font-medium text-slate-700 transition-colors ${className || ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <CalendarIcon size={14} className="text-slate-400 shrink-0 cursor-pointer hover:text-[var(--theme-primary)]" onClick={() => !disabled && setIsOpen(!isOpen)} />
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={() => setInputValue(displayValue)}
            disabled={disabled}
            placeholder={placeholder}
            maxLength="10"
            className={`flex-1 min-w-0 bg-transparent border-none outline-none ${inputValue ? 'text-[#0d0d0d]' : 'text-slate-500'} placeholder:text-slate-400 placeholder:opacity-100`}
          />
        </div>
      )}

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};
